// ============================================
// SHARED GOOGLE MAPS LOADER
// Every map page must use THIS hook so the Google Maps script is loaded
// exactly once and shared across the whole app. Mixing <LoadScript> and
// useJsApiLoader caused a "map spins forever until hard refresh" bug.
//
// FIX (2026-08-23): useJsApiLoader has a known race — on the FIRST visit
// the script's load event can fire before the loader attaches its callback,
// so isLoaded stays false forever (map spins until you navigate away and
// back, which re-creates the instance over an already-loaded script).
//
// FIX (2026-08-26): useJsApiLoader's isLoaded can ALSO flip true BEFORE the
// API is usable (the script's load event fires while google.maps.Map is still
// undefined), which crashed with "google.maps.Map is not a constructor".
// We now gate on the API + required libraries actually being present and
// ignore the loader's premature isLoaded entirely.
//
// FIX (2026-09-07): "The library directions is unknown" — there is NO Google
// Maps library called 'directions'. DirectionsService/DirectionsRenderer live
// in the 'routes' library (google.maps.RoutesLibrary). This was breaking every
// map page (client New Job, NewJobs, LiveMap, maps on quote/job screens, etc).
// ============================================

import { useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useState } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places', 'routes'];

// One consistent id means navigation between map pages reuses the loaded script.
const LOADER_ID = 'google-map-script';

// The ONLY reliable readiness signal: the base API AND both required
// libraries must actually exist. window.google.maps is defined BEFORE Map
// is a constructor, so checking just "google.maps exists" is NOT enough.
const isMapsFullyReady = () =>
  !!(
    typeof window !== 'undefined' &&
    window.google &&
    window.google.maps &&
    typeof window.google.maps.Map === 'function' &&
    window.google.maps.places &&
    typeof window.google.maps.DirectionsService === 'function'
  );

export function useGoogleMaps() {
  // Keep the loader active so it creates the script tag; we deliberately IGNORE
  // its isLoaded (fires early/never due to the load-event race) and instead
  // gate rendering on isMapsFullyReady().
  const { loadError } = useJsApiLoader({
    id: LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [mapsReady, setMapsReady] = useState(() => isMapsFullyReady());

  useEffect(() => {
    if (mapsReady) return;

    let stopped = false;
    const timer = setInterval(() => {
      if (isMapsFullyReady()) {
        if (!stopped) setMapsReady(true);
        clearInterval(timer);
      }
    }, 250);

    // Defensive: if the script tag is already in the DOM, listen for its
    // load event too (still gated on the robust readiness check).
    const script = document.getElementById(LOADER_ID);
    const onScriptLoad = () => {
      if (!stopped && isMapsFullyReady()) setMapsReady(true);
    };
    if (script && !script.dataset.ready) {
      script.addEventListener('load', onScriptLoad, { once: true });
      script.dataset.ready = '1';
    }

    return () => {
      stopped = true;
      clearInterval(timer);
      if (script) script.removeEventListener('load', onScriptLoad);
    };
  }, [mapsReady]);

  return { isLoaded: mapsReady, loadError };
}

export { GOOGLE_MAPS_API_KEY, LIBRARIES };